import {
  ListItem,
  Paper,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CheckCircleIcon, ImageIcon } from "lucide-react";
import React from "react";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";


// Main Chatresponse Component
const Chatresponse = ({
  index,
  message,
  handleCardSelect,
  selectedItems,
  setSelectedItems,
  selectedTags,
  handleTagSelect,
  availableTags,
  dispatchFunction, // pass dispatch function if needed further down the tree
}) => {
  // Parse text if the sender is bot
  const parsedData = message.sender === "bot" ? JSON.parse(message.text) : [];
  const isSelected = (item) => {
    return selectedItems.some(
      (selected) => JSON.stringify(selected) === JSON.stringify(item)
    );
  };

  // Component to render key-value pair
  const KeyValuePair = ({ k, v }) => (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography variant="body2" fontWeight="bold">
        {k}:
      </Typography>
      <Typography variant="body2">
        {typeof v === "object" ? renderValue(v) : v}
      </Typography>
    </Box>
  );

  // Helper to render objects
  const renderObject = (obj) => (
    <>
      {Object.entries(obj).map(([key, val]) => (
        <KeyValuePair key={key} k={key} v={val} />
      ))}
    </>
  );

  // Helper to render values recursively
  const renderValue = (value) => {
    if (Array.isArray(value)) {
      const isObjectArray = value.length > 0 && typeof value[0] === "object";
      if (isObjectArray) {
        return (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {value.map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <ChatCard
                  item={item}
                  isSelected={isSelected}
                  handleCardSelect={handleCardSelect}>
                  {renderObject(item)}
                </ChatCard>
              </Grid>
            ))}
          </Grid>
        );
      }
    }
    if (typeof value === "object" && value !== null) {
      return renderObject(value);
    }
    return value;
  };


  return (
    <ListItem
      key={index}
      sx={{
        justifyContent: message.sender === "bot" ? "flex-start" : "flex-end",
      }}>
      <Paper
        sx={{
          padding: 1.5,
          backgroundColor: message.sender === "bot" ? "#e0e0e0" : "#1976d2",
          color: message.sender === "bot" ? "black" : "white",
          maxWidth: "75%",
          borderRadius: "8px",
        }}>
        {message.sender === "user" && (
          <Box>
            {message.files?.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}>
                {message.files.map((file, idx) => (
                  <Chip
                    key={idx}
                    icon={
                      file.type === "image" ? (
                        <ImageIcon width={70} height={70} />
                      ) : file.type === "audio" ? (
                        <AudiotrackIcon width={70} height={70} />
                      ) : (
                        <InsertDriveFileIcon />
                      )
                    }
                    label={file.name}
                    sx={{ width: "150px", color: "white" }}
                    onClick={() => {
                      /* Add view functionality */
                    }}
                  />
                ))}
              </Box>
            )}
            <Typography sx={{ p: "5px" }}>{message.text}</Typography>
          </Box>
        )}

        {parsedData.map((item, itemIndex) => (
          <Box key={itemIndex} sx={{ mb: 2 }}>
            {item.file && (
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                File: {item.file}
              </Typography>
            )}

            {item.response && typeof item.response === "string" ? (
              <Typography>{item.response}</Typography>
            ) : Array.isArray(item.response) ? (
              <>{renderValue(item.response)}</>
            ) : item.response && typeof item.response === "object" ? (
              <ChatCard
                item={item.response}
                isSelected={isSelected}
                handleCardSelect={handleCardSelect}>
                {Object.entries(item.response || {}).map(([key, value]) => (
                  <Box key={key} sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {Array.isArray(value) ? null : `${key}:`}
                    </Typography>
                    {renderValue(value)}
                  </Box>
                ))}
              </ChatCard>
            ) : null}

            {item.openAIResponse && (
              <Box sx={{ mt: 1 }}>
                <ChatCard
                  item={item.openAIResponse}
                  isSelected={isSelected}
                  handleCardSelect={handleCardSelect}>
                  <Box sx={{ mt: 1 }}>{renderObject(item.openAIResponse)}</Box>
                </ChatCard>
              </Box>
            )}
          </Box>
        ))}

      </Paper>
    </ListItem>
  );
};

export default Chatresponse;

// ChatCard Component
const ChatCard = ({ isSelected, item, handleCardSelect, children, width }) => {
  return (
    <Paper
      sx={{
        width: width || "90%",
        p: 1.5,
        position: "relative",
        cursor: "pointer",
        border: 2,
        boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
        borderColor: isSelected(item) ? "green" : "transparent",
        backgroundColor: isSelected(item)
          ? "primary.lighter"
          : "background.default",
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleCardSelect(item);
      }}>
      {isSelected(item) && (
        <CheckCircleIcon
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            color: "green",
            fontSize: "1.2rem",
          }}
        />
      )}
      {children}
    </Paper>
  );
};

// Updated SaveDropdown Component with Dialog for name input